import React, { useState, useEffect, useMemo } from 'lib/react';
import {
  EVGrid, EVPaper, EVDownloadIcon, EVTabs, EVTab,
} from 'lib/components';
import Typography from 'commonComponents/typography';
import Button from 'lib/components/button';
import clsx from 'lib/clsx';
import useMediaQuery from 'hooks/useMediaQuery';
import { EXPORT_DATA_ROUTE } from 'constants/RouteConstants';
import history from 'store/CreateHistory';
import { useTranslation } from 'lib/translation';
import {
  fetchReportAnalytics as fetchReportAnalyticsAction,
  clearReportAnalytics as clearReportAnalyticsAction,
} from 'features/analyticsDashboard/AnalyticsDashboard.actions';
import { useDispatch, useSelector } from 'lib/redux';
import ComingSoon from 'commonComponents/comingSoonPage';
import { isRedirectExceptionOrgSelector } from 'utils/appSelectors.utils';
import { STATUS_LABELS, TAB_LIST } from './Analytics.constants';
import { getFilteredTabList } from './Analytics.utils';
import StatusCharts from './components/StatusCharts.component';
import Stats from './components/Stats.component';
import ChartsLoader from './components/ChartsLoader.component';
import AnalyticsStyles from './AnalyticsStyles';
import ClosedPercentageCharts from './components/ClosedPercentageCharts.component';
import AvgTurnCharts from './components/AvgTurnCharts.component';
import StatefarmCharts from './components/StatefarmCharts.component';

const TAB_CHART_COMPONENTS = {
  0: StatusCharts,
  2: AvgTurnCharts,
  5: ClosedPercentageCharts,
  8: StatefarmCharts,
};

function Analytics() {
  const classes = AnalyticsStyles();
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const showVerticalTabs = useMediaQuery(1065);

  const dispatch = useDispatch();

  const reportAnalyticsLoading = useSelector(({ analyticsDashboard }) => analyticsDashboard.loading.reportAnalytics);
  const reportAnalytics = useSelector(({ analyticsDashboard }) => analyticsDashboard.reportAnalytics);
  const reportsCount = useSelector(({ analyticsDashboard }) => analyticsDashboard.reportsCount);
  const isRedirectExceptionOrg = useSelector(isRedirectExceptionOrgSelector);

  const filteredTabList = useMemo(() => getFilteredTabList(isRedirectExceptionOrg, TAB_LIST), [isRedirectExceptionOrg]);

  const fetchReportAnalytics = () => dispatch(fetchReportAnalyticsAction());
  const clearReportAnalytics = () => dispatch(clearReportAnalyticsAction());

  useEffect(() => {
    fetchReportAnalytics();
  }, []);

  const handleTabChange = (_, id) => {
    setCurrentTab(id);
  };

  const SelectedChartComponent = TAB_CHART_COMPONENTS[filteredTabList[currentTab]?.id] || ComingSoon;

  const avgTurn = useMemo(() => {
    const completedReports = reportAnalytics.filter((report) => report.currentStatus === STATUS_LABELS.Completed);
    return completedReports.reduce((acc, item) => acc + item.turnaroundCalendarHours, 0) / completedReports.length;
  }, [reportAnalytics]);

  const avgTurnHours = avgTurn && new Date(avgTurn * 3600 * 1000).toISOString().substring(11, 11 + 5);

  const handleExportDataClick = () => {
    clearReportAnalytics();
    history.push(EXPORT_DATA_ROUTE);
  };

  return (
    <EVPaper elevation={0} className={classes.analyticsPaper}>
      <EVGrid className={classes.statsContainer}>
        <Stats value={reportsCount} label={t('analytics_dashboard__reports')} />
        <Stats value="" label="" />
        <Stats value={avgTurnHours} label={t('analytics_dashboard__avg_turn')} />
        <Stats value="100%" label={t('analytics_dashboard__sla_percentage')} />
        <Stats value="1.54%" label={t('analytics_dashboard__closure_rate')} />
        <Stats value="0.04%" label={t('analytics_dashboard__pending_rate')} />
        <EVGrid className={classes.exportDataBtnContainer}>
          <Button
            variant="contained"
            color="primary"
            className={classes.exportDataBtn}
            onClick={handleExportDataClick}
          >
            <EVDownloadIcon id="download-excel-icon" />
            {t('common__export_data')}
          </Button>
          <Typography variant="label-400" fontColor="charcoal-600">{t('analytics_dashboard__records_export_limit')}</Typography>
        </EVGrid>
      </EVGrid>
      <EVGrid container direction="column" style={{ gap: '24px', flex: 1 }}>
        <EVTabs
          orientation={showVerticalTabs ? 'vertical' : 'horizontal'}
          value={currentTab}
          onChange={handleTabChange}
          aria-label="tab-list"
          className={classes.tabList}
          classes={{ indicator: classes.indicator }}
          scrollButtons="off"
        >
          {filteredTabList.map((item) => (
            <EVTab
              key={item.id || item.label}
              data-testid={`tab-list-item-${item.id}`}
              className={clsx(classes.tabElement, (filteredTabList.length - 1 === item.id) && 'lastElement')}
              label={item.label}
              value={item.id}
            />
          ))}
        </EVTabs>
        <EVGrid className={classes.chartsContainer}>
          {reportAnalyticsLoading ? <ChartsLoader /> : <SelectedChartComponent /> }
        </EVGrid>
      </EVGrid>
    </EVPaper>
  );
}

export default Analytics;
